import { useState, useEffect } from 'react';
import { PhotoTagEntityType } from '@peles/shared';

export interface PhotoFiltersState {
  search: string;
  entityType: string;
  publishStatus: string; // '' | 'public' | 'landing' | 'private'
  /**
   * An `EventType` id — the website section a photograph is filed under.
   *
   * It narrows by the same `entityType`/`entityId` pair the Type filter uses,
   * which is why choosing one clears the other: the query can only carry one
   * kind of tag at a time, and leaving both set would silently drop whichever
   * lost rather than showing the visitor what happened.
   */
  eventTypeId: string;
}

interface PhotoFiltersProps {
  filters: PhotoFiltersState;
  onFiltersChange: (filters: PhotoFiltersState) => void;
  /** The office's own categories, from Settings → Event Types. */
  eventTypes?: readonly { id: string; name: string }[];
}

export function PhotoFilters({ filters, onFiltersChange, eventTypes = [] }: PhotoFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.search);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        onFiltersChange({ ...filters, search: searchInput });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, filters, onFiltersChange]);

  return (
    <div className="flex flex-wrap items-center gap-4 mb-4">
      <input
        type="text"
        placeholder="Search by caption..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full sm:w-64"
      />
      {eventTypes.length > 0 && (
        <select
          value={filters.eventTypeId}
          aria-label="Filter by category"
          onChange={(e) =>
            onFiltersChange({ ...filters, eventTypeId: e.target.value, entityType: '' })
          }
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Categories</option>
          {eventTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
      )}
      <select
        value={filters.entityType}
        aria-label="Filter by tag type"
        onChange={(e) =>
          onFiltersChange({ ...filters, entityType: e.target.value, eventTypeId: '' })
        }
        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        <option value="">All Types</option>
        <option value={PhotoTagEntityType.EVENT}>Events</option>
        <option value={PhotoTagEntityType.VENUE}>Venues</option>
        <option value={PhotoTagEntityType.VENDOR}>Vendors</option>
        <option value={PhotoTagEntityType.USER}>Users</option>
      </select>
      <select
        value={filters.publishStatus}
        onChange={(e) => onFiltersChange({ ...filters, publishStatus: e.target.value })}
        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        <option value="">All Photos</option>
        <option value="public">Public Only</option>
        <option value="landing">Landing Page</option>
        <option value="private">Private Only</option>
      </select>
    </div>
  );
}
